import React, { useCallback, useMemo } from "react";
// components
import HeaderAlertText from "@cart-client/components/review-item-list-wrapper/components/HeaderAlertText";
import HeaderCTA from "@cart-client/components/review-item-list-wrapper/components/HeaderCTA";
import { ReviewItemListCardProps } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard/types";
import { FoodCartItemWithUUID, IMCartItemWithUUID } from "@swiggy-private/cart-types";
// hooks
import { useGiftCartItemActions } from "@cart-client/hooks/useGiftCartItemActions";
// utils
import { buildFoodItemToRenderProps } from "@cart-client/helpers/GiftingFoodSubCart";
import { buildIMItemToRenderProps } from "@cart-client/helpers/GiftingIMSubCart";
import { isEmpty } from "@swiggy-private/checkout-core";
import { isItemAvailable } from "@swiggy-private/cart-ui";

interface UseGiftingStoreOOSCartProps {
    foodCartItemList?: FoodCartItemWithUUID[];
    imCartItemList?: IMCartItemWithUUID[];
}

export const useGetGiftingStoreOOSCart = ({ foodCartItemList, imCartItemList }: UseGiftingStoreOOSCartProps) => {
    // get oos items from the cart
    const foodOOSItems = useMemo(() => foodCartItemList?.filter(item => !isItemAvailable(item)), [foodCartItemList]);
    const imOOSItems = useMemo(() => imCartItemList?.filter(item => item.outOfStock === true), [imCartItemList]);
    const { onRemoveOOSItems } = useGiftCartItemActions({
        foodCartItemList: foodCartItemList || [],
        imCartItemList: imCartItemList || [],
    });
    const onRemoveOOSItemsListCallback = useCallback(() => {
        onRemoveOOSItems({ foodItemsToRemove: foodOOSItems, imItemsToRemove: imOOSItems });
    }, [foodOOSItems, imOOSItems, onRemoveOOSItems]);

    /**
     * Build the props for combined OOS Item List in Food and IM
     */
    const { items: foodItemOOSRenderProps } = useMemo(
        () =>
            buildFoodItemToRenderProps({
                items: foodOOSItems || [],
            }) || { items: [], restrictedQuantityItems: [] },
        [foodOOSItems],
    );

    /** for OOS cart, we want to combine restricted and available items */
    const { items: imItemOOSRenderProps } = useMemo(
        () =>
            buildIMItemToRenderProps({
                items: imOOSItems || [],
                combineRestrictedAndAvailableItems: true,
            }) || { items: [], restrictedQuantityItems: [] },
        [imOOSItems],
    );

    // build props for oos item list
    const propsForOOSItemList = useMemo(() => {
        if (isEmpty(foodItemOOSRenderProps) && isEmpty(imItemOOSRenderProps)) {
            return null;
        }
        return {
            header: {
                leftSection: {
                    text: <HeaderAlertText />,
                },
                rightSection: {
                    text: <HeaderCTA onClick={onRemoveOOSItemsListCallback} />,
                },
            },
            itemRenderer: {
                sections: [
                    {
                        items: [...(foodItemOOSRenderProps || []), ...(imItemOOSRenderProps || [])],
                    },
                ],
            },
        } as ReviewItemListCardProps;
    }, [foodItemOOSRenderProps, imItemOOSRenderProps, onRemoveOOSItemsListCallback]);

    return {
        propsForOOSItemList,
        oosItemCount: (foodOOSItems?.length || 0) + (imOOSItems?.length || 0),
        onRemoveOOSItemsListCallback,
    };
};
